import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { TrendingDown, TrendingUp, Target, Minus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Conversion Funnel widget — shows drop-off % between each step:
 * Home → Catalogo → Prodotto → Checkout → Ordine completato.
 *
 * Renders inside AnalyticsAdvanced. Period prop is controlled by parent.
 */
export const ConversionFunnelWidget = ({ period = "7d" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem("access_token");
        const res = await axios.get(`${API}/dashboard/conversion-funnel?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.warn("[funnel] fetch failed:", err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [period]);

  if (loading) return null;
  if (!data || !data.steps?.length) {
    return (
      <Card className="bg-[#0a0a0f] border border-white/10 text-gray-200" data-testid="funnel-empty">
        <CardContent className="p-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-amber-400 mb-4 flex items-center gap-2">
            <Target className="w-4 h-4" /> Funnel di Conversione
          </h3>
          <p className="text-gray-500 text-xs text-center py-6">
            Nessun dato di conversione disponibile per il periodo selezionato.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Max count for bar width
  const max = Math.max(...data.steps.map((s) => s.count), 1);

  // Find the worst drop-off step (excluding step 0 which has no prev)
  const worstStepIdx = data.steps
    .map((s, i) => ({ i, drop: i === 0 ? -1 : s.dropoff_from_prev }))
    .reduce((acc, cur) => (cur.drop > acc.drop ? cur : acc), { i: -1, drop: -1 }).i;

  return (
    <Card className="bg-[#0a0a0f] border border-white/10 text-gray-200" data-testid="funnel-widget">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-mono uppercase tracking-wider text-amber-400 flex items-center gap-2">
            <Target className="w-4 h-4" /> Funnel di Conversione
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-emerald-900/40 text-emerald-300 border border-emerald-700/40">
              Conversione totale: {data.overall_conversion_percent}%
            </Badge>
            {data.conversion_trend_percent != null && (
              <Badge
                className={`border ${
                  data.conversion_trend_percent > 0
                    ? "bg-emerald-900/30 text-emerald-300 border-emerald-700/40"
                    : data.conversion_trend_percent < 0
                      ? "bg-red-900/30 text-red-300 border-red-700/40"
                      : "bg-gray-800/40 text-gray-300 border-gray-700/40"
                }`}
                data-testid="funnel-overall-trend"
              >
                {data.conversion_trend_percent > 0 ? "↑" : data.conversion_trend_percent < 0 ? "↓" : "="}
                {Math.abs(data.conversion_trend_percent)}% vs periodo prec.
              </Badge>
            )}
          </div>
        </div>

        <div className="space-y-3" data-testid="funnel-steps">
          {data.steps.map((step, idx) => {
            const pct = (step.count / max) * 100;
            const isWorst = idx === worstStepIdx && step.dropoff_from_prev > 0;
            const trendIcon =
              step.trend_percent == null ? (
                <Minus className="w-3 h-3" />
              ) : step.trend_percent > 0 ? (
                <TrendingUp className="w-3 h-3" />
              ) : (
                <TrendingDown className="w-3 h-3" />
              );
            const trendColor =
              step.trend_percent == null
                ? "text-gray-500"
                : step.trend_percent > 0
                  ? "text-emerald-400"
                  : step.trend_percent < 0
                    ? "text-red-400"
                    : "text-gray-500";
            return (
              <div key={step.key} data-testid={`funnel-step-${step.key}`}>
                <div className="flex justify-between text-xs mb-1 gap-2 flex-wrap">
                  <span className="font-mono text-gray-300">{step.label}</span>
                  <span className="font-mono text-gray-100 flex items-center gap-3">
                    <span>{step.count}</span>
                    {step.trend_percent != null && (
                      <span
                        className={`flex items-center gap-1 text-[10px] ${trendColor}`}
                        title={`Periodo precedente: ${step.previous}`}
                        data-testid={`funnel-trend-${step.key}`}
                      >
                        {trendIcon}
                        {step.trend_percent > 0 ? "+" : ""}
                        {step.trend_percent}%
                      </span>
                    )}
                    {idx > 0 && (
                      <span
                        className={`flex items-center gap-1 text-[10px] ${
                          isWorst
                            ? "text-red-400 font-bold"
                            : step.dropoff_from_prev > 50
                              ? "text-orange-400"
                              : "text-gray-500"
                        }`}
                      >
                        <TrendingDown className="w-3 h-3" />
                        -{step.dropoff_from_prev}%
                      </span>
                    )}
                  </span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all duration-500 ${
                      isWorst
                        ? "bg-gradient-to-r from-red-500 to-red-600"
                        : "bg-gradient-to-r from-amber-400 to-amber-500"
                    }`}
                    style={{ width: `${Math.max(pct, 3)}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>

        {worstStepIdx > 0 && data.steps[worstStepIdx].dropoff_from_prev > 0 && (
          <p className="mt-4 text-xs text-amber-300/80 border-l-2 border-amber-500/60 pl-3" data-testid="funnel-insight">
            💡 <strong>Insight:</strong> il drop-off maggiore (-{data.steps[worstStepIdx].dropoff_from_prev}%) avviene
            al passaggio <strong>{data.steps[worstStepIdx - 1].label} → {data.steps[worstStepIdx].label}</strong>.
            Ottimizza quella pagina per migliorare la conversione totale.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
